import * as React from 'react';
import * as moment from 'moment';

import { TownNode, AreaNode, EmployeeNode } from '../../../schema';
import { nestedAccess } from 'sha-el-design/lib/helpers';
import '../Common/listPageStyles';

/**
 * Summary panel beside the Assign Work wizard.
 *
 * Previously every line was an <h4>, with <ol> lists nested INSIDE those
 * headings -- invalid markup that rendered town and area names at heading
 * weight, so nothing had visual hierarchy and the panel was hard to scan.
 * Now a label/value row list, with towns and areas as chips.
 */
const Row: React.StatelessComponent<{ label: string }> = (props) => (
  <div className='cs-wiz__review-row'>
    <span className='cs-wiz__review-label'>{props.label}</span>
    <span className='cs-wiz__review-value'>{props.children}</span>
  </div>
);

const Empty = () => <span className='cs-wiz__review-empty'>Not selected</span>;

const Chips: React.StatelessComponent<{ items: Array<{ name: string }> }> = (props) => {
  if (!props.items || !props.items.length) {
    return <Empty />;
  }
  return (
    <span className='cs-wiz__chips'>
      {props.items.map((v, i) => <span className='cs-wiz__chip' key={i}>{v.name}</span>)}
    </span>
  );
};

export const Review: React.StatelessComponent<Props> = (props) => {
  const days = props.deadline
    ? moment(props.deadline).diff(moment(props.dateAssigned), 'days')
    : 0;
  const pending = nestedAccess(props.caller, 'noOfWorksPending');

  return (
    <div>
      <Row label='State'>{props.state || <Empty />}</Row>
      <Row label='Caller'>{nestedAccess(props.caller, 'name') || <Empty />}</Row>
      <Row label={props.towns.length === 1 ? 'Town' : 'Towns'}>
        <Chips items={props.towns} />
      </Row>
      <Row label='Areas'>
        {props.areas.length
          ? <Chips items={props.areas} />
          : <span className='cs-wiz__review-empty'>Whole town</span>}
      </Row>
      <Row label='Date Assigned'>{moment(props.dateAssigned).format('D MMM YYYY')}</Row>
      <Row label='Deadline'>
        {props.deadline ? moment(props.deadline).format('D MMM YYYY') : <Empty />}
      </Row>
      <Row label='Duration'>{days} {days === 1 ? 'day' : 'days'}</Row>
      <Row label="Caller's open work">
        {pending === null || pending === undefined ? <Empty /> : pending}
      </Row>
    </div>
  );
};

interface Props {
  state: string;
  towns: TownNode[];
  areas: AreaNode[];
  caller: EmployeeNode;
  deadline: string;
  dateAssigned: string;
  userId: string;
}
